/**
 * LaunchCampaignBanners — iter164 visual, iter265 data-connected.
 *
 * Homepage launch-campaign banners. Titles/subtitles come from the
 * live `campaigns` collection via `GET /api/campaigns/public`, so
 * toggling a campaign to `is_active=false` in the Super Admin panel
 * makes the banner disappear on the next fetch. Visual scaffolding
 * (image, gradient, icon, CTA route) is kept locally per-slug because
 * the `campaigns` schema doesn't carry visual fields — those are
 * scaffolding, not campaign data. If the endpoint returns zero
 * matching campaigns, the section renders nothing (no fallback copy).
 */
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { ArrowRight, Building2, User, Phone, MessageCircle, X, UserRound } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { apiClient } from "../lib/api";

// Yanbu geo-gate (iter172) — kept unchanged.
const YANBU_LAT = 24.089;
const YANBU_LNG = 38.061;
const YANBU_RADIUS_KM = 50;
function _distKm(lat1, lng1, lat2, lng2) {
  const toRad = (d) => (d * Math.PI) / 180;
  const x = (toRad(lng2) - toRad(lng1)) * Math.cos(toRad((lat1 + lat2) / 2));
  const y = toRad(lat2) - toRad(lat1);
  return Math.sqrt(x * x + y * y) * 6371;
}

/* iter265 — Visual scaffolding keyed by campaign `slug`. The homepage
 * only renders a banner when a live campaign with a matching slug is
 * returned by `/api/campaigns/public`. Copy (title/subtitle) is read
 * from that live campaign's `name` and `description` fields — the
 * strings below are scaffolding only (image, colours, CTA route,
 * icon, en-label, en-fallback), never the campaign copy itself. */
const VISUAL_BY_SLUG = {
  yanbu_dealers_founders: {
    to: "/businesses/new",
    // iter302 — Yanbu campaign cover is the "شركة نبع الخليج للسيارات"
    // National Day 96 photo (updated), hosted on Emergent CDN.
    // Hardcoded (not DB-only) so the banner renders identically across
    // environments even when the production `campaigns` collection
    // hasn't been migrated with the new `cover_image_url` field.
    image:
      "https://customer-assets-eiarnc6j.emergentagent.net/job_carvo-market/artifacts/ldjjp8f1_80CF757A-02C0-4458-9839-92324377D484.png",
    accent: "#c9a55c",
    accent_soft: "#e8c873",
    bg: "linear-gradient(135deg,#0a0a0a 0%,#1a140a 55%,#3d2f0e 100%)",
    icon: Building2,
    label_ar: "عرض حصري لمعارض ينبع",
    label_en: "Yanbu Showrooms exclusive",
    title_en_fallback: "Yanbu Dealers Free Launch Offer",
    sub_en_fallback: "Register your showroom and unlock 3 full months free.",
    // iter165 marketing note — only for Yanbu.
    note_ar: "سيتم إطلاق عروض مماثلة لبقية مدن المملكة في مراحل الإطلاق القادمة.",
    note_en: "Similar offers will roll out to other Saudi cities in upcoming launch phases.",
    cta_ar: "سجل معرضك الآن",
    cta_en: "Register your showroom",
    testId: "campaign-banner-yanbu",
    // Geo-gated: only shown when the visitor is inside the Yanbu radius.
    geoGate: "yanbu",
    // iter302 — Contact defaults tied to this Yanbu banner. The DB
    // fields on the `campaigns` record win when present; these are
    // the environment-agnostic fallback.
    contact_person: "عبدالرحمن أحمد",
    contact_phone: "+966555360942",
    contact_whatsapp: "+966555360942",
  },
  beta_individual_plus: {
    to: "/register",
    image:
      "https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=1600&q=80&auto=format&fit=crop",
    accent: "#0ea5e9",
    accent_soft: "#7dd3fc",
    bg: "linear-gradient(135deg,#062033 0%,#0c3a5a 55%,#0e4a75 100%)",
    icon: User,
    label_ar: "عرض حصري للأفراد",
    label_en: "Individuals exclusive",
    title_en_fallback: "CARTAL Plus Individual Launch Offer",
    sub_en_fallback: "Register, verify, then activate 6 free months of CARTAL Plus.",
    cta_ar: "سجل حسابك الآن",
    cta_en: "Register your account",
    testId: "campaign-banner-individual",
  },
  // iter305 — Naba Al Khaleej showroom advertisement. Reuses the exact
  // same "full-photo ad" render path already built for the Yanbu
  // banner (isFullPhotoAd branch): clean full-bleed image, contact
  // button only, no CTA/link, no title/description overlay.
  //
  // iter329 — Geo-restricted to Yanbu only. Reuses the same
  // `geoGate: "yanbu"` gate the Yanbu-founders banner uses (GPS
  // distance ≤ YANBU_RADIUS_KM). Fail-closed: geolocation denied /
  // unavailable / timed-out keeps `yanbuVisible === false`, so the
  // ad is filtered out at line 159 BEFORE it is ever returned from
  // the `visible` list — the DOM never gets the node.
  naba_al_khaleej_showroom: {
    to: null,
    image:
      "https://customer-assets-eiarnc6j.emergentagent.net/job_carvo-market/artifacts/ldjjp8f1_80CF757A-02C0-4458-9839-92324377D484.png",
    accent: "#c9a55c",
    accent_soft: "#e8c873",
    bg: "linear-gradient(135deg,#0a0a0a 0%,#1a140a 55%,#3d2f0e 100%)",
    icon: Building2,
    label_ar: "شركة نبع الخليج للسيارات",
    label_en: "Naba Al Khaleej Cars",
    title_en_fallback: "Naba Al Khaleej Cars",
    sub_en_fallback: "",
    cta_ar: "",
    cta_en: "",
    testId: "campaign-banner-naba",
    // Geo-gated: only shown when the visitor's GPS resolves inside
    // the Yanbu radius. Matches the acceptance rule "Yanbu only, any
    // other city or unknown location = hidden".
    geoGate: "yanbu",
    // Fallback contact metadata (DB-authoritative when present).
    contact_person: "عبدالرحمن أحمد",
    contact_phone: "+966555360942",
    contact_whatsapp: "+966555360942",
  },
};

export default function LaunchCampaignBanners() {
  const { lang } = useI18n();

  // iter301 — Contact modal (fed by DB fields on the campaign record:
  //   `contact_person`, `contact_phone`, `contact_whatsapp`,
  //   `cover_image_url`). The button appears only for campaigns that
  //   carry contact_person + a phone/whatsapp — no code-level toggles.
  const [contactModal, setContactModal] = useState(null);

  // Yanbu geo-gate (unchanged).
  const [yanbuVisible, setYanbuVisible] = useState(false);
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.geolocation) return;
    let cancelled = false;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (cancelled) return;
        const d = _distKm(pos.coords.latitude, pos.coords.longitude, YANBU_LAT, YANBU_LNG);
        if (d <= YANBU_RADIUS_KM) setYanbuVisible(true);
      },
      () => { /* denied / unavailable / timeout — stay hidden, silently */ },
      { enableHighAccuracy: false, timeout: 6000, maximumAge: 24 * 60 * 60 * 1000 },
    );
    return () => { cancelled = true; };
  }, []);

  // iter265 — Live campaigns from backend.
  const [campaigns, setCampaigns] = useState(null); // null = loading, [] = loaded empty
  useEffect(() => {
    let cancelled = false;
    apiClient
      .get("/campaigns/public", { params: { marketing: 1 } })
      .then(({ data }) => { if (!cancelled) setCampaigns(data?.items || []); })
      .catch(() => { if (!cancelled) setCampaigns([]); });
    return () => { cancelled = true; };
  }, []);

  // Loading & empty states — render nothing (avoid CLS + no stale hardcoded copy).
  if (campaigns === null || campaigns.length === 0) return null;

  // Match each live campaign to its visual scaffolding by slug, then
  // apply the Yanbu geo-gate if the visual declares one.
  const visible = campaigns
    .map((c) => {
      const v = VISUAL_BY_SLUG[c.slug];
      if (!v) return null;
      if (v.geoGate === "yanbu" && !yanbuVisible) return null;
      return { campaign: c, visual: v };
    })
    .filter(Boolean);

  if (visible.length === 0) return null;

  return (
    <section className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-8" data-testid="launch-campaign-banners">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {visible.map(({ campaign: c, visual: b }) => {
          const Icon = b.icon;
          // Titles/subtitles: DB-authoritative. Backend stores a single
          // `name` + `description` (Arabic in current records). The
          // English fallbacks in the visual scaffolding are used only
          // when lang=en and no localised copy exists on the record.
          const titleAr = c.name || "";
          const titleEn = c.name_en || b.title_en_fallback;
          const subAr = c.description || "";
          const subEn = c.description_en || b.sub_en_fallback;
          // iter305 — Full-photo advertisement banners (Yanbu campaign
          // + Naba Al Khaleej showroom): image is the ONLY visual
          // layer, no outer dark gradient, no title/description
          // overlay, no CTA, only the contact button.
          const isYanbu = c.slug === "yanbu_dealers_founders" || c.slug === "naba_al_khaleej_showroom";
          const OuterTag = isYanbu ? "div" : Link;
          const outerProps = isYanbu ? {} : { to: b.to };
          return (
            <OuterTag
              key={c.id}
              {...outerProps}
              className="relative group block overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-shadow"
              style={{
                background: isYanbu ? "#0a0a0a" : b.bg,
                border: `1px solid ${b.accent}55`,
                minHeight: "260px",
              }}
              data-testid={b.testId}
              aria-label={lang === "ar" ? titleAr : titleEn}
            >
              <img
                src={c.cover_image_url || b.image}
                alt=""
                loading="lazy"
                decoding="async"
                referrerPolicy="no-referrer"
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${isYanbu ? "opacity-100" : "opacity-60 group-hover:opacity-70"}`}
              />
              {!isYanbu && (
                <div
                  className="absolute inset-0"
                  style={{
                    background: `linear-gradient(${lang === "ar" ? "270deg" : "90deg"}, ${b.bg.match(/#[0-9a-fA-F]{6}/)[0]}00 0%, ${b.bg.match(/#[0-9a-fA-F]{6}/)[0]}dd 55%, ${b.bg.match(/#[0-9a-fA-F]{6}/)[0]}f5 100%)`,
                  }}
                />
              )}
              {isYanbu && (
                <div
                  className="absolute inset-x-0 bottom-0 h-1/2 pointer-events-none"
                  style={{
                    background: "linear-gradient(180deg,rgba(10,10,10,0) 0%,rgba(10,10,10,0.55) 60%,rgba(10,10,10,0.85) 100%)",
                  }}
                />
              )}
              <div
                className="absolute -top-10 -end-10 w-56 h-56 rounded-full opacity-25 blur-2xl"
                style={{ background: b.accent }}
                aria-hidden
              />

              <div className="relative p-6 sm:p-8 flex flex-col h-full min-h-[260px]">
                {!isYanbu && (
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                      style={{
                        background: `${b.accent}22`,
                        border: `1px solid ${b.accent}77`,
                      }}
                    >
                      <Icon className="w-5 h-5" style={{ color: b.accent_soft }} strokeWidth={2.2} />
                    </div>
                    <div
                      className="text-[10px] uppercase tracking-[0.25em] font-extrabold"
                      style={{ color: b.accent_soft }}
                    >
                      {lang === "ar" ? b.label_ar : b.label_en}
                    </div>
                  </div>
                )}

                {!isYanbu && (
                  <>
                    <h3
                      className="font-display text-2xl sm:text-3xl font-black leading-snug mb-2"
                      style={{ color: "#f7eecd" }}
                    >
                      {lang === "ar" ? titleAr : titleEn}
                    </h3>
                    <p
                      className="text-sm sm:text-base leading-relaxed max-w-md"
                      style={{ color: "rgba(247,238,205,0.78)" }}
                    >
                      {lang === "ar" ? subAr : subEn}
                    </p>
                    {b.note_ar && (
                      <p
                        className="text-[11px] sm:text-xs mt-2 italic leading-relaxed max-w-md"
                        style={{ color: "rgba(247,238,205,0.55)" }}
                        data-testid={`${b.testId}-note`}
                      >
                        {lang === "ar" ? b.note_ar : b.note_en}
                      </p>
                    )}
                  </>
                )}

                <div className="mt-auto pt-6 flex items-center flex-wrap gap-2.5">
                  {!isYanbu && (
                    <span
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold shadow-md group-hover:shadow-lg group-hover:translate-y-[-1px] transition"
                      style={{
                        background: `linear-gradient(135deg, ${b.accent} 0%, ${b.accent_soft} 100%)`,
                        color: "#0a0a0a",
                      }}
                      data-testid={`${b.testId}-cta`}
                    >
                      {lang === "ar" ? b.cta_ar : b.cta_en}
                      <ArrowRight className="w-4 h-4 rtl:rotate-180" strokeWidth={2.4} />
                    </span>
                  )}
                  {(() => {
                    // iter302 — DB wins when present; scaffolding fallback ensures
                    // the button renders in every environment even before the
                    // `campaigns` collection is migrated.
                    const person = c.contact_person || b.contact_person;
                    const phone = c.contact_phone || b.contact_phone;
                    const whatsapp = c.contact_whatsapp || b.contact_whatsapp;
                    if (!person || (!phone && !whatsapp)) return null;
                    return (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setContactModal({
                            person,
                            phone: phone || "",
                            whatsapp: whatsapp || phone || "",
                            accent: b.accent,
                            accent_soft: b.accent_soft,
                          });
                        }}
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-extrabold backdrop-blur-md transition hover:translate-y-[-1px]"
                        style={{
                          background: "rgba(10,10,10,0.55)",
                          color: "#f7eecd",
                          border: `1.5px solid ${b.accent}`,
                          boxShadow: `0 6px 20px -8px ${b.accent}, inset 0 0 0 1px rgba(247,238,205,0.12)`,
                        }}
                        data-testid={`${b.testId}-contact-btn`}
                        aria-label={lang === "ar" ? "تواصل معنا" : "Contact us"}
                      >
                        <Phone className="w-4 h-4" strokeWidth={2.4} />
                        {lang === "ar" ? "تواصل معنا" : "Contact us"}
                      </button>
                    );
                  })()}
                </div>
              </div>
            </OuterTag>
          );
        })}
      </div>

      {contactModal && (
        <ContactModal
          data={contactModal}
          lang={lang}
          onClose={() => setContactModal(null)}
        />
      )}
    </section>
  );
}

// iter301 — Premium contact modal used by the campaign banner. Renders
// the employee's name plus a direct-call button and a WhatsApp button.
// Numbers are normalised to `+` international format so `tel:` and
// `wa.me/` work reliably from mobile devices.
function ContactModal({ data, lang, onClose }) {
  const { person, phone, whatsapp, accent, accent_soft } = data;
  const digits = (s) => String(s || "").replace(/[^0-9+]/g, "");
  const telHref = phone ? `tel:${digits(phone)}` : null;
  const waDigits = digits(whatsapp).replace(/^\+/, "");
  const waHref = waDigits ? `https://wa.me/${waDigits}` : null;

  // Close on ESC
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <>
      <button
        type="button"
        aria-label="close"
        onClick={onClose}
        className="fixed inset-0 z-[9998] bg-black/60 backdrop-blur-sm"
        data-testid="campaign-contact-backdrop"
      />
      <div
        role="dialog"
        aria-modal="true"
        className="fixed inset-x-4 top-24 sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 sm:max-w-md sm:w-full z-[9999] rounded-3xl overflow-hidden shadow-2xl"
        style={{
          background: "linear-gradient(160deg,#0a0a0a 0%,#1a140a 55%,#2a1e0a 100%)",
          border: `1.5px solid ${accent}66`,
          boxShadow: `0 30px 80px -20px ${accent}55, 0 0 0 1px rgba(247,238,205,0.06)`,
        }}
        data-testid="campaign-contact-modal"
      >
        <div
          className="absolute -top-16 -end-16 w-64 h-64 rounded-full opacity-25 blur-3xl pointer-events-none"
          style={{ background: accent }}
          aria-hidden
        />
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClose(); }}
          aria-label={lang === "ar" ? "إغلاق" : "Close"}
          className="absolute top-3 end-3 w-10 h-10 rounded-full flex items-center justify-center transition hover:scale-105 z-10"
          style={{ background: "rgba(247,238,205,0.12)", color: "#f7eecd" }}
          data-testid="campaign-contact-close"
        >
          <X className="w-5 h-5" strokeWidth={2.6} />
        </button>

        <div className="relative px-6 pt-8 pb-6 text-center">
          <div className="text-[10px] uppercase tracking-[0.3em] font-extrabold mb-2" style={{ color: accent_soft }}>
            {lang === "ar" ? "تواصل مباشر" : "Direct contact"}
          </div>
          <div
            className="mx-auto w-20 h-20 rounded-full flex items-center justify-center mb-3"
            style={{
              background: `linear-gradient(135deg, ${accent} 0%, ${accent_soft} 100%)`,
              boxShadow: `0 12px 30px -8px ${accent}`,
            }}
          >
            <UserRound className="w-9 h-9 text-[#0a0a0a]" strokeWidth={2.2} />
          </div>
          <div className="font-display text-2xl font-black" style={{ color: "#f7eecd" }} data-testid="campaign-contact-person">
            {person}
          </div>

          <div className="mt-6 grid gap-3">
            {telHref && (
              <a
                href={telHref}
                className="w-full inline-flex items-center justify-center gap-2.5 px-5 py-3.5 rounded-2xl text-[15px] font-black shadow-lg transition hover:translate-y-[-1px]"
                style={{
                  background: `linear-gradient(135deg, ${accent} 0%, ${accent_soft} 100%)`,
                  color: "#0a0a0a",
                }}
                data-testid="campaign-contact-call"
              >
                <Phone className="w-5 h-5" strokeWidth={2.6} />
                <span dir="ltr">{lang === "ar" ? "اتصال مباشر" : "Call"} · {digits(phone)}</span>
              </a>
            )}
            {waHref && (
              <a
                href={waHref}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full inline-flex items-center justify-center gap-2.5 px-5 py-3.5 rounded-2xl text-[15px] font-black shadow-lg transition hover:translate-y-[-1px]"
                style={{
                  background: "linear-gradient(135deg,#25D366 0%,#128C7E 100%)",
                  color: "#ffffff",
                }}
                data-testid="campaign-contact-whatsapp"
              >
                <MessageCircle className="w-5 h-5" strokeWidth={2.6} />
                <span dir="ltr">{lang === "ar" ? "واتساب" : "WhatsApp"} · {digits(whatsapp).replace(/^\+/, "")}</span>
              </a>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
